import { toast } from "sonner";
import { JotformSubmission } from "@/pages/reports/types";

interface JotformApiOptions {
  apiKey?: string;
  baseUrl?: string;
}

interface JotformApiResponse {
  responseCode: number;
  message: string;
  content: any;
  resultSet?: {
    offset: number;
    limit: number;
    count: number;
  };
}

class JotformApi {
  private apiKey: string;
  private baseUrl: string;
  private isEuAccount: boolean;
  private formTitlesCache: Record<string, string>;
  private submissionsCache: Record<string, {
    data: JotformSubmission[];
    timestamp: number;
  }>;
  private activeRequests: Record<string, Promise<any>>;

  constructor(options: JotformApiOptions = {}) {
    this.apiKey = options.apiKey || "";
    this.baseUrl = options.baseUrl || "https://api.jotform.com";
    this.isEuAccount = false;
    this.formTitlesCache = {};
    this.submissionsCache = {};
    this.activeRequests = {};
  }

  setApiKey(apiKey: string) {
    this.apiKey = apiKey;
  }

  setEuEndpoint(isEu: boolean) {
    this.isEuAccount = isEu;
    this.baseUrl = isEu ? "https://eu-api.jotform.com" : "https://api.jotform.com";
    console.log(`JotformApi now using ${this.baseUrl} endpoint`);
  }

  private async request<T>(endpoint: string, params: Record<string, string> = {}): Promise<T> {
    if (!this.apiKey) {
      throw new Error("API key is required. Call setApiKey() first.");
    }

    const queryParams = new URLSearchParams({ ...params, apiKey: this.apiKey }).toString();
    const url = `${this.baseUrl}${endpoint}${queryParams ? `?${queryParams}` : ""}`;
    
    // Create a request key based on the URL
    const requestKey = url;
    
    // If we already have an active request for this URL, return the promise to prevent duplicate requests
    if (this.activeRequests[requestKey]) {
      console.log('Reusing in-flight request for:', url);
      return this.activeRequests[requestKey];
    }

    console.log('Making request to Jotform API:', url);

    try {
      // Reduced timeout from 15s to 10s for faster error response
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      
      // Create the request promise
      this.activeRequests[requestKey] = (async () => {
        try {
          const response = await fetch(url, { 
            signal: controller.signal,
            // Remove cache-control headers that cause CORS issues
            // headers: {
            //   'Cache-Control': 'max-age=600',
            //   'Pragma': 'no-cache',
            // }
          });
          
          clearTimeout(timeoutId);
          
          if (!response.ok) {
            const errorText = await response.text();
            console.error('Jotform API response not ok:', response.status, errorText);
            
            if (response.status === 429 || errorText.includes('rate limit')) {
              throw new Error("ERR_INSUFFICIENT_RESOURCES: Rate limit exceeded. Try again later.");
            }
            
            throw new Error(`HTTP error! status: ${response.status}, details: ${errorText}`);
          }
          
          const data: JotformApiResponse = await response.json();
          
          if (data.responseCode === 301 && data.message && data.message.includes("EU Safe mode")) {
            console.log("Switching to EU endpoint for Jotform API");
            this.setEuEndpoint(true);
            
            // Remove this request from activeRequests to allow a retry
            delete this.activeRequests[requestKey];
            return this.request(endpoint, params);
          }

          if (data.responseCode !== 200) {
            console.error('Jotform API error:', data.message);
            throw new Error(data.message || "An error occurred while fetching data from Jotform");
          }

          return data.content as T;
        } finally {
          // Clean up the active request regardless of success or failure
          delete this.activeRequests[requestKey];
        }
      })();
      
      return await this.activeRequests[requestKey];
    } catch (error: any) {
      // Always clean up active requests on error
      delete this.activeRequests[requestKey];
      
      if (error.name === 'AbortError') {
        throw new Error('Request timed out. The Jotform API did not respond in time.');
      }
      
      if (error.message?.includes('ERR_INSUFFICIENT_RESOURCES') || 
          error.toString().includes('ERR_INSUFFICIENT_RESOURCES') ||
          error.message?.includes('rate limit')) {
        console.error("Jotform API request failed due to insufficient resources:", error);
        throw new Error("ERR_INSUFFICIENT_RESOURCES: The API ran out of resources. Try again later.");
      }
      
      console.error("Jotform API request failed:", error);
      throw error;
    }
  }

  async getFormTitle(formId: string): Promise<string> {
    if (this.formTitlesCache[formId]) {
      return this.formTitlesCache[formId];
    }
    
    try {
      const formData = await this.request<any>(`/form/${formId}`, {});
      if (formData && formData.title) {
        this.formTitlesCache[formId] = formData.title;
        console.log(`Cached form title for ${formId}: ${formData.title}`);
        return formData.title;
      }
    } catch (err) {
      console.error(`Failed to fetch form title for ${formId}:`, err);
    }
    
    return `Form ${formId}`;
  }

  async getFormSubmissions(formId: string, limit = 25, offset = 0): Promise<JotformSubmission[]> {
    console.log(`Fetching submissions for form ${formId} with limit ${limit}`);
    
    // Check cache first (valid for 5 minutes)
    const cacheKey = `${formId}-${limit}-${offset}`;
    const cachedData = this.submissionsCache[cacheKey];
    const now = Date.now();
    
    // Reduced cache time from 5 minutes to 2 minutes for more frequent refreshes
    if (cachedData && (now - cachedData.timestamp) < 120000) { 
      console.log(`Using cached submissions for form ${formId}`);
      return cachedData.data;
    }
    
    try {
      // Fetch form title in parallel - don't block the submissions request
      const formTitlePromise = this.formTitlesCache[formId] 
        ? Promise.resolve(this.formTitlesCache[formId])
        : this.getFormTitle(formId).catch(() => `Form ${formId}`);
      
      // Reduced limit from 50 to 25 to make it even faster
      const submissions = await this.request<{ [key: string]: any }>(
        `/form/${formId}/submissions`,
        {
          limit: limit.toString(),
          offset: offset.toString(),
          orderby: "created_at",
          direction: "DESC" // Get newest first
        }
      );
      
      console.log(`Received ${Object.keys(submissions).length} submissions for form ${formId}`);

      // Resolve the form title that we've been fetching in parallel
      const formTitle = await formTitlePromise;
      this.formTitlesCache[formId] = formTitle;

      const processedSubmissions = Object.values(submissions).map((submission) => {
        let submittedAt = "";
        
        try {
          const submissionDateFromAnswers = this.extractSubmissionDate(submission);
          if (submissionDateFromAnswers) {
            submittedAt = submissionDateFromAnswers;
          }
          else if (submission.created_at && !isNaN(Number(submission.created_at))) {
            submittedAt = new Date(Number(submission.created_at) * 1000).toISOString();
          } 
          else if (typeof submission.created_at === 'string') {
            submittedAt = submission.created_at;
          }
          else {
            console.warn(`Unable to determine submission date for ${submission.id}`);
            submittedAt = new Date().toISOString();
          }
        } catch (e) {
          console.error("Error processing submission date:", submission.created_at, e);
          submittedAt = new Date().toISOString();
        }
        
        return {
          id: submission.id,
          form_id: submission.form_id,
          form_title: formTitle,
          ip: submission.ip || "",
          created_at: submission.created_at,
          status: submission.status || "",
          answers: submission.answers || {},
          title: this.extractTitle(submission),
          content: this.extractContent(submission),
          team_id: "",
          submitted_by: "",
          updated_at: submission.updated_at || submission.created_at,
          submitted_at: submittedAt,
          image_url: this.extractImageUrl(submission)
        };
      });
      
      // Store in cache
      this.submissionsCache[cacheKey] = {
        data: processedSubmissions,
        timestamp: now
      };
      
      return processedSubmissions;
    } catch (error) {
      console.error(`Error fetching submissions for form ${formId}:`, error);
      throw error;
    }
  }
  
  private extractSubmissionDate(submission: any): string | null {
    const dateFields = ['submission_date', 'date', 'submitted_date', 'submission_time', 'datetime'];
    
    for (const answer of Object.values(submission.answers || {})) {
      const answerObj = answer as any;
      const name = answerObj.name?.toLowerCase();
      
      if (name && dateFields.some(field => name.includes(field))) {
        if (typeof answerObj.answer === 'string' && answerObj.answer.trim()) {
          return answerObj.answer;
        }
      }
    }
    
    return null;
  }

  async getMultipleFormSubmissions(formIds: string[]): Promise<JotformSubmission[]> {
    if (!formIds.length) {
      console.warn('No form IDs provided to getMultipleFormSubmissions');
      return [];
    }
    
    // Increased to 3 for parallel requests - higher concurrency for better speed
    const maxConcurrentRequests = 3;
    const results: JotformSubmission[][] = [];
    
    console.log(`Fetching submissions for ${formIds.length} forms with max ${maxConcurrentRequests} concurrent requests`);
    
    // Process form IDs in smaller batches
    for (let i = 0; i < formIds.length; i += maxConcurrentRequests) {
      const batch = formIds.slice(i, i + maxConcurrentRequests);
      
      try {
        const batchResults = await Promise.all(
          batch.map(async (formId) => {
            try {
              // Reduced limit to 15 to avoid resource issues and make it faster
              return await this.getFormSubmissions(formId, 15);
            } catch (error) {
              console.error(`Failed to fetch submissions for form ${formId}:`, error);
              toast.error(`Failed to fetch form ${formId}: ${error instanceof Error ? error.message : 'Unknown error'}`);
              return [];
            }
          })
        );
        
        results.push(...batchResults);
        
        // Reduced delay between batches from 1000ms to 800ms
        if (i + maxConcurrentRequests < formIds.length) {
          await new Promise(resolve => setTimeout(resolve, 800));
        }
      } catch (error) {
        console.error(`Failed to fetch batch of form submissions:`, error);
      }
    }
    
    const allSubmissions = results.flat();
    console.log(`Total submissions from all forms: ${allSubmissions.length}`);
    
    return allSubmissions;
  }

  private extractTitle(submission: any): string {
    const titleKeys = ['title', 'subject', 'incident_title', 'report_title'];
    
    for (const answer of Object.values(submission.answers || {})) {
      const answerObj = answer as any;
      const name = answerObj.name?.toLowerCase();
      
      if (name && titleKeys.some(key => name.includes(key))) {
        return answerObj.answer || '';
      }
    }
    
    for (const answer of Object.values(submission.answers || {})) {
      const answerObj = answer as any;
      if (typeof answerObj.answer === 'string' && answerObj.answer.trim()) {
        return answerObj.answer;
      }
    }
    
    return `Submission ${submission.id}`;
  }

  private extractContent(submission: any): string {
    const contentKeys = ['description', 'details', 'content', 'message', 'notes', 'comments'];
    
    for (const answer of Object.values(submission.answers || {})) {
      const answerObj = answer as any;
      const name = answerObj.name?.toLowerCase();
      
      if (name && contentKeys.some(key => name.includes(key))) {
        return answerObj.answer || '';
      }
    }
    
    const textAnswers = Object.values(submission.answers || {})
      .map((answer: any) => {
        if (typeof answer.answer === 'string') {
          return answer.answer.trim();
        }
        return '';
      })
      .filter(Boolean)
      .join(' | ');
    
    return textAnswers || '';
  }

  private extractImageUrl(submission: any): string | undefined {
    for (const answer of Object.values(submission.answers || {})) {
      const answerObj = answer as any;
      
      if (answerObj.type === 'control_fileupload' && answerObj.answer) {
        const files = Array.isArray(answerObj.answer) ? answerObj.answer : [answerObj.answer];
        
        for (const file of files) {
          if (typeof file === 'string' && (
            file.endsWith('.jpg') || 
            file.endsWith('.jpeg') || 
            file.endsWith('.png') || 
            file.endsWith('.gif')
          )) {
            return file;
          }
        }
      }
    }
    
    return undefined;
  }

  async getForms(limit = 100, offset = 0): Promise<any[]> {
    const forms = await this.request<{ [key: string]: any }>(
      '/user/forms',
      {
        limit: limit.toString(),
        offset: offset.toString()
      }
    );

    const formsArray = Object.values(forms);
    formsArray.forEach(form => {
      if (form.id && form.title) {
        this.formTitlesCache[form.id] = form.title;
      }
    });

    return formsArray;
  }
  
  // Clear cache method for manual cache invalidation
  clearCache() {
    this.submissionsCache = {};
    console.log('Jotform submissions cache cleared');
  }
}

const jotformApi = new JotformApi();

export default jotformApi;
